import React, {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import Main from 'ripple/Containers/Main';
import useDiscoverHooks from 'pages/Discover/useDiscoverHooks';
import strip from 'strip';
import { useMediaQuery } from 'react-responsive';
import useIsMobile from 'lib/useIsMobile';
import LoadBlur from 'components/LoadBlur';
import HeroCarousel from 'containers/HeroCarousel';
import Grid from '@mui/material/Grid';
import CardActions from '@mui/material/CardActions';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import Button from '@mui/material/Button';
import moment from 'moment';
import { canSetupParty, hasSetupParty, getSelectionDate } from 'lib/eventUtils';
import IconButton from '@mui/material/IconButton';
import Fab from '@mui/material/Fab';
import { useRouter } from 'found';
import { useApolloClient } from '@apollo/client';
import apiGateway from '../../lib/apiGateway';
import EventTimer from './EventCountdown';
import JoinConversation from './JoinConversation';
import WhatInPack from './WhatInPack';
import Info from './Info';
import AddToCalendarButtonTooltip from './AddToCalendarTooltip';
import { useAuthContext } from '../../AuthContext';
import { GET_PROFILE_INFO } from '../../schema/apolloQueries/commonQueries';

// eslint-disable-next-line import/no-unresolved
const Application = React.lazy(() => import('application/Application'));
// eslint-disable-next-line import/no-unresolved
const Activities = React.lazy(() => import('activities/Activities'));
// eslint-disable-next-line import/no-unresolved
const Feed = React.lazy(() => import('feed/Feed'));

const styles = {
  name: {
    fontSize: '30px',
    fontWeight: '700',
    lineHeight: '54px',
    fontFamily: 'NewOrderBold',
  },
  nameMobile: {
    fontSize: '22px',
    fontWeight: '700',
    lineHeight: '36px',
    fontFamily: 'NewOrderBold',
  },
  subHeader: {
    fontWeight: '700',
    fontSize: '50px',
    lineHeight: '54px',
    color: '#0076BB',
    fontFamily: 'NewOrderBold',
  },
  subHeaderMobile: {
    fontWeight: '700',
    fontSize: '32px',
    lineHeight: '36px',
    color: '#0076BB',
    fontFamily: 'NewOrderBold',
  },
  application: {
    fontWeight: '700',
    fontSize: '22px',
    lineHeight: '30px',
    fontFamily: 'NiveauGroteskRegular',
    textAlign: 'left',
  },
  applicationMobile: {
    fontWeight: '700',
    fontSize: '16px',
    lineHeight: '24px',
    fontFamily: 'NiveauGroteskRegular',
    textAlign: 'left',
  },
  orangeColor: {
    fontFamily: 'NiveauGroteskRegular',
    color: '#0076BB',
  },
  eventDescription: {
    fontWeight: '400',
    fontSize: '22px',
    lineHeight: '30px',
    fontFamily: 'NiveauGroteskRegular',
    marginTop: '1em',
  },
  eventDescriptionMobile: {
    fontWeight: '400',
    fontSize: '16px',
    lineHeight: '24px',
    fontFamily: 'NiveauGroteskRegular',
  },
  whatInPackContainer: {
    fontWeight: '400',
    fontSize: '22px',
    lineHeight: '28px',
    cursor: 'pointer',
    fontFamily: 'NiveauGroteskRegularItal',
    color: '#0076BB',
    fontStyle: 'italic',
    textTransform: 'initial',
    border: '1px solid #0076BB',
    borderRadius: '50px',
  },
  whatInPackContainerMobile: {
    fontWeight: '400',
    fontSize: '14px',
    lineHeight: '14px',
    cursor: 'pointer',
    fontFamily: 'NiveauGroteskRegularItal',
    color: '#0076BB',
    fontStyle: 'italic',
    textTransform: 'initial',
    border: '1px solid #0076BB',
    borderRadius: '50px',
  },
  backToTop: {
    fontWeight: '400',
    fontSize: '14px',
    lineHeight: '14px',
    cursor: 'pointer',
    position: 'fixed',
    right: 10,
    bottom: 60,
    fontFamily: 'NiveauGroteskRegularItal',
    color: '#0076BB',
    fontStyle: 'italic',
    textTransform: 'initial',
    border: '1px solid #0076BB',
    borderRadius: '50px',
    backgroundColor: 'white',
  },
};

const NewEvent = ({ relay }) => {
  const {
    match: {
      params: { eventSlug },
    },
  } = useRouter();
  const mobile = useIsMobile();
  const tablet = useMediaQuery({ maxWidth: 960 });
  const { showSignInDialog } = useAuthContext();
  const activitiesList = useRef(null);
  const [isSetup, setIsSetup] = useState(false);
  const [showPartyCard, setShowPartyCard] = useState(false);
  const [eventId, setEventId] = useState();
  const [activitiesRendered, setActivitiesRendered] = useState(false);
  const [packsAvailable, setPacksAvailable] = useState(0);
  const [membersApplied, setMembersApplied] = useState(0);
  const [eventDetails, setEventDetails] = useState({});
  const [rewardDetails, setRewardDetails] = useState({});
  const { loading } = useDiscoverHooks(relay);
  const [showPage, setShowPage] = useState(false);
  const [heroPlaylist, setHeroPlaylist] = useState({});
  const [openWhatInPack, setOpenWhatInPack] = useState(false);
  const [openInfo, setOpenInfo] = useState(false);
  const [applicationMF, setApplicationMF] = useState(null);
  const applicationBottomRef = useRef(null);
  const feedRef = useRef(null);
  const [feedMF, setFeedMF] = useState(null);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [showAddToCalendarTooltip, setShowAddToCalendarTooltip] = useState(
    false,
  );

  const client = useApolloClient();

  const profileData = client.readQuery({
    query: GET_PROFILE_INFO,
  });
  const personId = profileData?.getProfile
    ? profileData?.getProfile?.personId
    : null;
  const userId = profileData?.getProfile
    ? profileData?.getProfile?.personId
    : 0;

  useEffect(() => {
    setShowPage(false);
    apiGateway.getEventDetailsBySlug(eventSlug).then(res => {
      if (res && res.heroPlayList) {
        setEventDetails(res);
        setEventId(res.id);
        setIsSetup(hasSetupParty(res));
        setShowPartyCard(isSetup || canSetupParty(res));
        // const obj = {};
        // obj.id = res.id;
        // obj.autoplay = res.heroPlayList?.autoPlay || false;
        // let items = [];
        // if (res.heroPlayList.assets && res.heroPlayList.assets.length > 0) {
        //   const itms = [];
        //   res.heroPlayList.assets.forEach(val => {
        //     itms.push({ asset: { url: val.hlsPlaylistUrl } });
        //   });
        //   items = itms;
        //   obj.items = items;
        // }
        setHeroPlaylist(res.heroPlayList);
        setShowPage(true);
        apiGateway.getRewardDetailsByEventId(res.id).then(rdetails => {
          setRewardDetails(rdetails);
        });
        apiGateway.getNumberOfPacksAndParticipants(res.id).then(packs => {
          setPacksAvailable(packs?.noOfPack || 0);
          setMembersApplied(packs?.noOfApplicant || 0);
        });
      }
    });
  }, [userId, eventSlug, isSetup, profileData?.getProfile]);

  const handleActivityCompletion = useCallback(
    ({ detail: { tasks } }) => {
      tasks.forEach(feedItem => {
        let relayVariable = '';
        switch (feedItem) {
          case 'photos':
            relayVariable = 'photoPageSize';
            break;
          case 'reviews':
            relayVariable = 'reviewPageSize';
            break;
          default:
            break;
        }
        if (relayVariable) {
          relay.forceFetch({ [relayVariable]: 10 });
        }
      });
    },
    [relay],
  );

  const removeGlobalEventListeners = useCallback(() => {
    document.removeEventListener('ActivityCompleted', handleActivityCompletion);
  }, [handleActivityCompletion]);

  const addGlobalEventListeners = useCallback(() => {
    removeGlobalEventListeners();
    document.addEventListener('ActivityCompleted', handleActivityCompletion);
  }, [handleActivityCompletion, removeGlobalEventListeners]);

  useEffect(() => {
    if (personId) {
      if (activitiesList.current && !activitiesRendered && showPage) {
        const stdActivities = [];
        setActivitiesRendered(
          <Suspense fallback='loading...'>
            <Activities
              context={{
                eventId,
                sponsorId: eventDetails?.sponsorId,
                userId: personId,
                standardActivities: stdActivities,
                themeType: 'light',
                eventType: eventDetails?.eventType,
                eventCategory: (eventDetails?.categories || []).map(c => c.name),
              }}
            />
          </Suspense>,
        );
      }
      addGlobalEventListeners();
    }
    return () => {
      removeGlobalEventListeners();
    };
  }, [
    showPage,
    activitiesList,
    eventId,
    eventDetails,
    personId,
    eventDetails?.sponsorId,
    showPartyCard,
    isSetup,
    addGlobalEventListeners,
    removeGlobalEventListeners,
    activitiesRendered,
  ]);

  const metaTitle = useMemo(() => {
    return eventDetails
      ? `${strip(eventDetails?.community?.name)} ${strip(eventDetails?.name)}`
      : '';
  }, [eventDetails]);

  // const metaDescription = useMemo(() => {
  //   return eventDetails
  //     ? `${
  //       eventDetails?.summary
  //           ? strip(eventDetails?.summary)
  //           : truncate(strip(eventDetails?.description || 'Frito-Lay® and FOX Sports are teaming up to bring you the Ultimate Soccer Watch Party! They&apos;ve got your watch party plans covered with ever #sponsored'), {
  //               length: 140,
  //               separator: ' ',
  //             })
  //       } #sponsored`
  //     : '';
  // }, [eventDetails]);

  // const heroAssetUrl = useMemo(
  //   () => (eventDetails
  //       ? get(eventDetails, 'heroPlaylist.items[0].asset.url')
  //       : ''),
  //   [eventDetails],
  // );
  const onWhatInPackClose = () => {
    setOpenWhatInPack(!openWhatInPack);
  };
  const onInfoClose = () => {
    setOpenInfo(!openInfo);
  };
  const meta = useMemo(
    () => [
      {
        property: 'og:type',
        content: 'article',
      },
      {
        property: 'og:tag',
        content: 'sponsored',
      },
      {
        property: 'og:title',
        content: 'Frito-Lay® and FOX Sports Ultimate Soccer Watch Party',
      },
      {
        property: 'og:description',
        content:
          'Frito-Lay® and FOX Sports are teaming up to bring you the Ultimate Soccer Watch Party! They&apos;ve got your watch party plans covered with ever #sponsored',
      },
      {
        property: 'og:image',
        content:
          'https://media.ripplestreet.com/basset/ac77c8e0430cf80e94bc0aabee3300af/original/Program_Banner.jpg',
      },
      {
        property: 'twitter:card',
        content: 'summary_large_image',
      },
      {
        property: 'twitter:title',
        content: 'Frito-Lay® and FOX Sports Ultimate Soccer Watch Party',
      },
      {
        property: 'twitter:description',
        content:
          'Frito-Lay® and FOX Sports are teaming up to bring you the Ultimate Soccer Watch Party! They&apos;ve got your watch party plans covered with ever #sponsored',
      },
      {
        property: 'twitter:image',
        content:
          'https://media.ripplestreet.com/basset/ac77c8e0430cf80e94bc0aabee3300af/original/Program_Banner.jpg',
      },
    ],
    [],
  );
  const afterConvSubmit = () => {
    setFeedMF(null);
  };

  useEffect(() => {
    if (!applicationMF && showPage && profileData && eventDetails) {
      setApplicationMF(
        <Suspense fallback='Loading'>
          <Application
            context={{
              eventId,
              personId,
              eventType: eventDetails?.eventType,
              sponsorId: eventDetails?.community?.id,
              loggedIn: profileData?.getProfile?.loggedIn,
              email: profileData?.getProfile?.personInfo?.email,
              showSignInDialog,
              categories: (eventDetails?.categories || []).map(c => c.name),
              eventOptinConfiguration: eventDetails?.eventOptinConfiguration,
              selectionDate: getSelectionDate(eventDetails),
            }}
            renderToRefs={[applicationBottomRef.current]}
            callback={setActivitiesRendered}
          />
        </Suspense>,
      );
    }
  }, [
    showPage,
    applicationMF,
    eventDetails,
    eventId,
    personId,
    showSignInDialog,
    profileData,
    applicationBottomRef,
  ]);

  useEffect(() => {
    if (feedRef.current && !feedMF && showPage && profileData) {
      setFeedMF(
        <Suspense fallback='Loading Feed...'>
          <Feed
            context={{
              modelType: 'Event',
              eventId,
              eventSlug: eventDetails?.slug,
              personId,
              sponsorId: eventDetails?.community?.id,
              eventType: eventDetails?.eventType,
              loggedIn: profileData?.getProfile?.loggedIn,
              email: profileData?.getProfile?.personInfo?.email,
              showSignInDialog,
              enabledFeedTypes: ['SocialAsset', 'Discussion', 'FAQ'],
            }}
          />
        </Suspense>,
      );
    }
  }, [
    showPage,
    eventDetails,
    eventId,
    personId,
    showSignInDialog,
    profileData,
    feedMF,
  ]);

  const handleScroll = () => {
    const position = window.pageYOffset;
    setScrollPosition(position);
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  const getGameDate = i => {
    if (i === 0) {
      return new Date('2023-07-21 21:00');
    }
    if (i === 1) {
      return new Date('2023-07-26 21:00');
    }
    return new Date('2023-08-05 21:00');
  };

  return (
    <>
      <Info open={openInfo} onClose={onInfoClose} />
      <WhatInPack
        item={
          rewardDetails?.rewards
            ? rewardDetails?.rewards[0]?.rewardItems || []
            : []
        }
        open={openWhatInPack}
        onClose={onWhatInPackClose}
      />
      <LoadBlur loading={!showPage}>
        <Main
          loading={loading}
          meta={meta}
          style={{ marginTop: '10%' }}
          title={metaTitle}
        >
          {showPage && (
            <div style={{ background: 'white', padding: '3%', color: 'black' }}>
              <Grid container spacing={3}>
                {mobile && (
                  <Grid item lg={6} md={12} sm={12} xs={12}>
                    <HeroCarousel playlist={heroPlaylist} compact={false} />
                  </Grid>
                )}
                <Grid
                  item
                  lg={4}
                  md={12}
                  sm={12}
                  xs={12}
                  style={{ textAlign: mobile ? 'center' : 'left' }}
                >
                  <div
                    style={mobile ? styles.subHeaderMobile : styles.subHeader}
                  >
                    {eventDetails?.name}
                  </div>
                  <EventTimer />
                  <div
                    style={
                      mobile ? styles.applicationMobile : styles.application
                    }
                  >
                    Taking Applications{' '}
                    <span style={styles.orangeColor}>
                      {eventDetails.milestones
                      && eventDetails.milestones.applicationStartDate
                        ? moment(
                            eventDetails.milestones.applicationStartDate,
                          ).format('MM/DD')
                        : 'x/xx'}
                    </span>{' '}
                    -{' '}
                    <span style={styles.orangeColor}>
                      {eventDetails.milestones
                      && eventDetails.milestones.applicationEndDate
                        ? moment(
                            eventDetails.milestones.applicationEndDate,
                          ).format('MM/DD')
                        : 'x/xx'}
                    </span>
                  </div>
                  <div
                    style={
                      mobile ? styles.applicationMobile : styles.application
                    }
                  >
                    <span style={styles.orangeColor}>{packsAvailable}</span>{' '}
                    Packs Available |{' '}
                    <span style={styles.orangeColor}>{membersApplied}</span>{' '}
                    Members Applied
                  </div>
                  &nbsp;
                  <Grid item lg={7} md={12} sm={12} xs={12}>
                    <CardActions>
                      <div style={{ width: '100%' }}>{applicationMF}</div>
                    </CardActions>
                  </Grid>
                </Grid>
                {!mobile && (
                  <Grid item lg={8} md={12} sm={12} xs={12}>
                    <HeroCarousel playlist={heroPlaylist} compact={false} />
                  </Grid>
                )}
              </Grid>
              <Grid
                sx={{
                  width: 'calc(100% ) !important',
                  marginLeft: '0 !important',
                }}
                container
                spacing={3}
              >
                <Grid item xs={12}>
                  <div
                    style={
                      mobile
                        ? styles.eventDescriptionMobile
                        : styles.eventDescription
                    }
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{
                      __html: eventDetails?.description,
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant='outlined'
                    style={
                      mobile
                        ? styles.whatInPackContainerMobile
                        : styles.whatInPackContainer
                    }
                    onClick={() => {
                      setOpenWhatInPack(true);
                    }}
                  >
                    What&apos;s in the Party Pack?
                    <ArrowRightIcon style={{ fontSize: '24' }} />
                  </Button>
                </Grid>
                <Grid container sx={{ mt: 2 }}>
                  {rewardDetails
                    && rewardDetails.rewards
                    && rewardDetails.rewards.length > 0
                    && rewardDetails.rewards.map((rwd, i) => {
                      return (
                        <Grid
                          item
                          lg={4}
                          md={12}
                          sm={12}
                          xs={12}
                          style={{
                            marginBottom: (() => {
                              if (!mobile && !tablet) {
                                return i < rewardDetails.rewards.length - 1
                                  ? 10
                                  : 0;
                              }
                              return 0;
                            })(),
                            borderRight: (() => {
                              if (!mobile && !tablet) {
                                return i < rewardDetails.rewards.length - 1
                                  ? '1px solid'
                                  : '';
                              }
                              return '';
                            })(),
                            borderTop: mobile || tablet ? '1px solid' : '',
                            borderBottom:
                              (mobile || tablet)
                              && i === rewardDetails.rewards.length - 1
                                ? '1px solid'
                                : '',
                            paddingTop: mobile || tablet ? '2%' : '',
                            paddingBottom: mobile || tablet ? '1%' : '',
                            position: 'relative',
                          }}
                        >
                          {showAddToCalendarTooltip === i && (
                            <AddToCalendarButtonTooltip
                              calendarEvent={{
                                title: rwd?.name || '',
                                description: rwd?.description || '',
                                startDate: getGameDate(i),
                                durationInMinutes: 120,
                                address: 'My Home',
                              }}
                              callBack={() => setShowAddToCalendarTooltip(-1)}
                            />
                          )}
                          <IconButton
                            onClick={() => {
                              if (showAddToCalendarTooltip === i) {
                                setShowAddToCalendarTooltip(-1);
                              } else {
                                setShowAddToCalendarTooltip(i);
                              }
                            }}
                            style={{
                              height: '83px',
                              width: '80px',
                              verticalAlign: 'top',
                              marginRight: '2%',
                              marginLeft: '2%',
                              cursor: 'pointer',
                            }}
                            size='large'
                          >
                            <img
                              alt='itemimage'
                              src={rwd.asset?.hlsPlaylistUrl}
                              style={{ width: '83px' }}
                            />
                          </IconButton>
                          <div style={{ display: 'inline-block' }}>
                            <div
                              style={{
                                fontWeight: '700',
                                fontSize: mobile ? '16px' : '22px',
                                lineHeight: mobile ? '24px' : '30px',
                                color: '#0076BB',
                                fontFamily: 'NiveauGroteskRegular',
                              }}
                            >
                              {rwd.name}
                            </div>
                            <div
                              style={{
                                fontWeight: '400',
                                fontSize: mobile ? '16px' : '22px',
                                lineHeight: mobile ? '24px' : '30px',
                                fontFamily: 'NiveauGroteskRegular',
                                marginBottom: '0px',
                              }}
                              // eslint-disable-next-line react/no-danger
                              dangerouslySetInnerHTML={{ __html: rwd.notes }}
                            />
                            <div
                              style={{
                                fontWeight: '400',
                                fontSize: mobile ? '16px' : '22px',
                                lineHeight: mobile ? '24px' : '30px',
                                fontFamily: 'NiveauGroteskRegular',
                                marginBottom: '0px',
                              }}
                              // eslint-disable-next-line react/no-danger
                              dangerouslySetInnerHTML={{
                                __html: rwd.description,
                              }}
                            />
                            <div
                              style={{
                                fontWeight: '400',
                                fontSize: mobile ? '16px' : '22px',
                                lineHeight: mobile ? '24px' : '30px',
                                color: '#0076BB',
                                fontFamily: 'NiveauGroteskRegularItal',
                                fontStyle: 'italic',
                              }}
                              // eslint-disable-next-line react/no-danger
                              dangerouslySetInnerHTML={{
                                __html: rwd.shortDescription,
                              }}
                            />
                          </div>
                        </Grid>
                      );
                    })}
                </Grid>
                <Grid container spacing={1}>
                  <Grid
                    item
                    xs={12}
                    style={{
                      fontWeight: '700',
                      fontSize: mobile ? '22px' : '32px',
                      lineHeight: '28px',
                      marginTop: '3%',
                      fontFamily: 'NiveauGroteskRegular',
                      paddingRight: mobile ? '2%' : '',
                      paddingLeft: mobile ? '2%' : '',
                    }}
                  >
                    Event Activities
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    style={{
                      fontWeight: '400',
                      fontSize: mobile ? '16px' : '22px',
                      lineHeight: mobile ? '24px' : '28px',
                      marginTop: '1%',
                      fontFamily: 'NiveauGroteskRegular',
                      paddingRight: mobile ? '4%' : '',
                      paddingLeft: mobile ? '4%' : '',
                    }}
                  >
                    What are activities, anyway? Activities are a way to show
                    our brand partners your excitement and event participation
                    efforts! Completing activities increases your chances of
                    getting selected for this and future Ripple Street events.
                  </Grid>
                  <Grid item xs={12}>
                    <div ref={activitiesList} style={{ margin: '0px 0 20px' }}>
                      {activitiesRendered}
                    </div>
                  </Grid>
                </Grid>
                <Grid container spacing={2}>
                  <Grid
                    item
                    xs={12}
                    style={{
                      marginBottom: '1%',
                      marginLeft: mobile ? '5%' : '',
                      marginRight: mobile ? '5%' : '',
                    }}
                  >
                    <JoinConversation
                      loggedIn={profileData?.getProfile?.loggedIn}
                      showSignInDialog={showSignInDialog}
                      eventDetails={eventDetails}
                      perId={personId}
                      afterConvSubmit={afterConvSubmit}
                      placeholder='Your text here...'
                    />
                  </Grid>
                  {mobile && (
                    <Grid item lg={6} md={12} sm={12} xs={12}>
                      <CardActions>
                        <div
                          ref={applicationBottomRef}
                          style={{ width: '100%' }}
                        />
                      </CardActions>
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <div ref={feedRef} style={{ margin: '0px 0 20px' }}>
                      {feedMF}
                    </div>
                  </Grid>
                </Grid>
                {scrollPosition > 100 && (
                  <Fab
                    variant='extended'
                    style={styles.backToTop}
                    onClick={() => window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })}
                  >
                    Back to top
                    <ArrowDropUpIcon style={{ fontSize: '40' }} />
                  </Fab>
                )}
              </Grid>
            </div>
          )}
        </Main>
      </LoadBlur>
    </>
  );
};

NewEvent.propTypes = {
  relay: PropTypes.object.isRequired,
};

export default NewEvent;
